import { GridReadyEvent } from 'ag-grid-community';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
import 'ag-grid-community/dist/styles/ag-theme-dark.css';
import { AgGridReact } from 'ag-grid-react';
import React, { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import AgGridBuilder from '../../../../Components/AgGridBuilder/AgGridBuilder';
import {
    Currency,
    DemeterSymbolContractModel,
    DemeterSymbolModel,
    DemeterUserStoreType,
    ExchangeType,
    SymbolCategory,
    UnitOfMeasure,
} from '../../../../Generated/Raven-Demeter';
import { useApplicationSelector } from '../../../../Redux/ReduxStore';
import { MarketPriceModel, selectMarketPricesBySymbol } from '../../../../Redux/Slices/MarketPricesSlice';
import { selectStoreId } from '../../../../Redux/Slices/SystemSlice';
import { selectUserRealtimeExchanges } from '../../../../Redux/Slices/UserSlice';
import useCurrencyConversionApi from '../../../Apis/Hooks/useCurrencyConversionApiHook';
import useSymbolsApi from '../../../Apis/Hooks/useSymbolsApiHook';
import useUnitOfMeasureConversionApi from '../../../Apis/Hooks/useUnitOfMeasureConversionApiHook';
import Switch from '../../../Components/Form/Buttons/Switch';
import TableSkeleton from '../../../Components/Layout/TableSkeleton';
import { IExchangeCommoditySelection } from '../../../Components/Navigation/Hooks/useExchangeCommodityNavigationHook';
import ShowMoreAccordian from '../../../Components/Tables/ShowMoreAccordian/ShowMoreAccordian';
import formattingService from '../../../Services/Formatting/FormattingService';
import useLanguage from '../../../Services/Language/useLanguageHook';
import lightstreamerMarketPricesService from '../../../Services/MarketPrices/LightstreamerMarketPricesService';
import lightstreamerRealtimeMarketPricesService from '../../../Services/MarketPrices/LightstreamerRealtimeMarketPricesService';
import FuturesPricesChart from './FuturesPricesChart';
import FuturesPricesComparisonChart from './FuturesPricesComparisonChart';
import FuturesPricesForwardCurveChart from './FuturesPricesForwardCurveChart';
import styles from './FuturesPricesTable.module.scss';
import {
    ExchangeSettings,
    futuresPricesColumnDefinitions,
    futuresPricesColumnOptions,
    RendererParameters,
    SymbolContractCompositeModel,
} from './FuturesPricesTableDefinitions';

export interface IFuturesPricesTableProps {
    exchangeCommoditySelection: IExchangeCommoditySelection;
    currency?: Currency;
    unitOfMeasure?: UnitOfMeasure;
}

export type SymbolContract = DemeterSymbolContractModel & MarketPriceModel & { id: string; multipler: number };

const rowsToShow = 12;
const defaultContractNumber = 1;
const selectionLimit = 5;
const hideRollingContractCategories: SymbolCategory[] = [SymbolCategory.Currency];

const FuturesPricesTable: React.FC<IFuturesPricesTableProps> = (props: IFuturesPricesTableProps) => {
    const [translations] = useLanguage();

    // Table hooks.
    const gridReference: MutableRefObject<AgGridReact | null> = useRef<AgGridReact>(null);
    const [onGridReady, setOnGridReady] = useState<GridReadyEvent>();

    // Data hooks.
    const storeId = useApplicationSelector(selectStoreId);
    const marketPricesBySymbol = useApplicationSelector(selectMarketPricesBySymbol);
    const realtimeExchanges = useApplicationSelector(selectUserRealtimeExchanges);

    // Symbols hooks.
    const symbols = useSymbolsApi();
    const [symbolModel, setSymbolModel] = useState<DemeterSymbolModel>();
    const symbolContractCompositeModelsReference = useRef<SymbolContractCompositeModel[]>([]);
    const [symbolContractCompositeModels, setSymbolContractCompositeModels] = useState<SymbolContractCompositeModel[]>(
        symbolContractCompositeModelsReference.current,
    );
    const [symbolContractSelections, setSymbolContractSelections] = useState<SymbolContractCompositeModel[]>([]);

    // Filter hooks.
    const [getCurrencyConversionRate, currencyConversionMap] = useCurrencyConversionApi(symbolModel?.reutersInstrumentCodePrefix);
    const [getUnitOfMeasureConversionRate, unitOfMeasureConversionMap] = useUnitOfMeasureConversionApi(symbolModel?.reutersInstrumentCodePrefix);
    const [showMore, setShowMore] = useState<boolean>(false);

    // Service hooks.
    const selectedLightstreamerMarketPricesService = useMemo(
        // eslint-disable-next-line no-confusing-arrow
        () =>
            realtimeExchanges.some((x) => x === props.exchangeCommoditySelection.exchange)
                ? lightstreamerRealtimeMarketPricesService
                : lightstreamerMarketPricesService,
        [realtimeExchanges, props.exchangeCommoditySelection.exchange],
    );

    // We need to create this part of the table definition here because of the local selectRow function.
    const actionsCellRenderer = (parameters: RendererParameters) => (
        <Switch
            disabled={symbolContractSelections?.length === selectionLimit && !parameters.data.selected}
            checked={parameters.data.selected}
            handleChange={() => {
                selectRow(parameters.data.rowIndex, true);
            }}
            track={{
                eventCategory: 'Futures',
                // eslint-disable-next-line max-len
                eventAction: `FuturesPriceTableRow_${props.exchangeCommoditySelection.exchange}_${parameters.data.name}_${parameters.data.reutersInstrumentCode}_SelectionChanged`,
            }}
        />
    );

    const currentColumnDefinitions = useMemo(() => {
        const hasTradeRegistration = ExchangeSettings[props.exchangeCommoditySelection.exchange as ExchangeType].hasTradeRegistrationColumns;
        const hasPreviousSettlementPrice = ExchangeSettings[props.exchangeCommoditySelection.exchange as ExchangeType].hasPreviousSettlementPriceColumn;
        const columns = futuresPricesColumnDefinitions({
            hasTradeRegistration: hasTradeRegistration ?? false,
            hasPreviousSettlementPrice: hasPreviousSettlementPrice ?? false,
        });
        columns[columns.findIndex((x) => x.colId === 'actions')].cellRenderer = actionsCellRenderer;
        return columns;
    }, [props.exchangeCommoditySelection, symbolContractSelections]);

    useEffect(() => {
        if (!symbols) {
            return;
        }

        const selectedSymbol = symbols.find(
            (x) => x.exchange === props.exchangeCommoditySelection.exchange && x.commodity === props.exchangeCommoditySelection.commodity,
        );

        if (selectedSymbol) {
            setSymbolModel(selectedSymbol);
        }
    }, [symbols, props.exchangeCommoditySelection]);

    // Tell the lightstreamer service to get new data if the commodity changes.
    useEffect(() => {
        if (!symbolModel?.symbolContracts || !selectedLightstreamerMarketPricesService) {
            return;
        }

        const codes = symbolModel.symbolContracts.map((x) => x.reutersInstrumentCode);
        selectedLightstreamerMarketPricesService.start(storeId, codes);
    }, [symbolModel, selectedLightstreamerMarketPricesService]);

    // Stop the lightstreamer service when we leave this component.
    useEffect(
        () => () => {
            lightstreamerMarketPricesService.stop(storeId);
            lightstreamerRealtimeMarketPricesService.stop(storeId);
        },
        [],
    );

    const convertMarketPrice = (contract: SymbolContractCompositeModel): SymbolContractCompositeModel => {
        const convert = (value: number | undefined) => {
            if (value === undefined) {
                return undefined;
            }
            return getUnitOfMeasureConversionRate(props.unitOfMeasure) * getCurrencyConversionRate(props.currency, contract.year, contract.month) * value;
        };

        return {
            ...contract,
            ...{
                contractYearMonth: `${contract.year}${contract.month}`,
                latestPrice: contract.latestPrice ?? undefined,
                latestPriceConverted: convert(contract.latestPrice),
                latestOrSettledPriceConverted: convert(contract.latestPrice) ?? convert(contract.settlementPrice),
                bidPriceConverted: convert(contract.bidPrice),
                askPriceConverted: convert(contract.askPrice),
                settlementPriceConverted: convert(contract.settlementPrice),
                netChangeConverted: convert(contract.netChange),
                openPriceConverted: convert(contract.openPrice),
                expirationDateFormatted: formattingService.toShortDayMonthYear(contract.expirationDate),
                lastSessionHighPriceConverted: convert(contract.lastSessionHighPrice),
                lastSessionLowPriceConverted: convert(contract.lastSessionLowPrice),
                settlementNetChangeConverted: convert(contract.settlementNetChange),
                highPriceConverted: convert(contract.highPrice),
                lowPriceConverted: convert(contract.lowPrice),
                tradeRegistrationPriceConverted: convert(contract.tradeRegistrationPrice),
                currency: props.currency ?? contract.currency,
            },
        };
    };

    // Update the initial symbol contract when we selected a different symbol.
    useEffect(() => {
        if (!symbolModel?.symbolContracts) {
            return;
        }

        if (symbolModel.symbolContracts) {
            const contracts = symbolModel.symbolContracts.map((row, index) => {
                const contract: SymbolContractCompositeModel = {
                    id: `${index}`,
                    rowIndex: index,
                    selected: false,
                    multipler: symbolModel.priceMultiplierFutures,
                    reutersInstrumentCodePrefix: symbolModel.reutersInstrumentCodePrefix,
                    ...row,
                };

                const marketPrice = marketPricesBySymbol[row.reutersInstrumentCode];

                if (!marketPrice) {
                    return contract;
                }

                return convertMarketPrice({
                    ...contract,
                    ...marketPrice,
                });
            });

            setSymbolContractCompositeModels(contracts);
            setSymbolContractSelections(contracts.filter((x) => x.selected));
            symbolContractCompositeModelsReference.current = contracts;
            selectRow((symbolModel.rollingContractNumber ?? defaultContractNumber) - 1);
        }
    }, [symbolModel]);

    // Update the prices when they come in.
    useEffect(() => {
        if (symbolContractCompositeModels.length === 0 || !currencyConversionMap || !unitOfMeasureConversionMap) {
            return;
        }

        const contracts = symbolContractCompositeModels.map((row) => {
            const marketPrice = marketPricesBySymbol[row.reutersInstrumentCode];

            if (!marketPrice) {
                return row;
            }

            return convertMarketPrice({
                ...row,
                ...marketPrice,
            });
        });

        setSymbolContractCompositeModels(contracts);
        symbolContractCompositeModelsReference.current = contracts;
    }, [marketPricesBySymbol, currencyConversionMap, props.currency, unitOfMeasureConversionMap, props.unitOfMeasure]);

    useEffect(() => {
        if (!onGridReady || !gridReference?.current?.columnApi || !gridReference?.current?.api) {
            return;
        }

        selectRow((symbolModel?.rollingContractNumber ?? defaultContractNumber) - 1, false, true);
    }, [gridReference?.current, onGridReady]);

    useEffect(() => {
        if (!gridReference.current) {
            return;
        }

        if (ExchangeSettings[props.exchangeCommoditySelection.exchange as ExchangeType].hasPreviousSettlementPriceColumn) {
            gridReference.current.columnApi.applyColumnState({
                state: [{ colId: 'previousSettlementPrice', hide: false }],
            });
        } else {
            gridReference.current.columnApi.applyColumnState({
                state: [{ colId: 'previousSettlementPrice', hide: true }],
            });
        }
    }, [gridReference?.current, props.exchangeCommoditySelection]);

    const selectRow = (rowIndex: number, allowMultipleSelections?: boolean, setSelected?: boolean) => {
        if (!gridReference.current?.columnApi || !gridReference.current?.api) {
            return;
        }

        const hasTradeRegistration = ExchangeSettings[props.exchangeCommoditySelection.exchange as ExchangeType].hasTradeRegistrationColumns;
        const hasPreviousSettlementPrice = ExchangeSettings[props.exchangeCommoditySelection.exchange as ExchangeType].hasPreviousSettlementPriceColumn;
        const columns = futuresPricesColumnDefinitions({
            hasTradeRegistration: hasTradeRegistration ?? false,
            hasPreviousSettlementPrice: hasPreviousSettlementPrice ?? false,
        });
        const actionFieldName = columns[columns.length - 1].field ?? '';
        const actionColumn = gridReference.current?.columnApi.getColumn(actionFieldName);
        const selectedRows = gridReference?.current?.api?.getSelectedRows();
        if (!allowMultipleSelections) {
            (symbolContractCompositeModelsReference?.current ?? []).forEach((symbolContractModel) => {
                const rowNode = gridReference?.current?.api?.getRowNode(symbolContractModel.id);

                if (symbolContractModel.rowIndex !== rowIndex && rowNode) {
                    symbolContractModel.selected = false;
                    rowNode.setSelected(false);
                    if (actionColumn) {
                        rowNode.setDataValue(actionFieldName, false);
                    }
                }
            });
        }

        const row = symbolContractCompositeModelsReference?.current[rowIndex];

        if (row) {
            row.selected = !row.selected || (!allowMultipleSelections && (selectedRows?.length ?? 0) > 1) || !!setSelected;
            const selectedRow = gridReference?.current?.api?.getRowNode(row.id);
            selectedRow?.setSelected(row.selected);
            if (actionColumn) {
                selectedRow?.setDataValue(actionFieldName, !!row.selected || (!allowMultipleSelections && (selectedRows?.length ?? 0) > 1));
            }
        }

        symbolContractCompositeModelsReference.current = [...symbolContractCompositeModelsReference.current];
        setSymbolContractCompositeModels(symbolContractCompositeModelsReference.current);
        setSymbolContractSelections(symbolContractCompositeModelsReference.current.filter((x) => x.selected));
    };

    return (
        <div className={styles.futures_prices_table_container}>
            {symbolContractCompositeModels.length === 0 || symbolContractCompositeModelsReference.current.length === 0 ? (
                <TableSkeleton />
            ) : (
                <>
                    <AgGridBuilder
                        gridRef={gridReference}
                        rowData={showMore ? symbolContractCompositeModels : symbolContractCompositeModels.slice(0, rowsToShow)}
                        hasSaveColumnsState
                        columnDefinitions={currentColumnDefinitions}
                        defaultColumnDefinition={futuresPricesColumnOptions}
                        domLayout="autoHeight"
                        userStoreType={DemeterUserStoreType.CommodityFuturesTable}
                        cellClickedHandler={(event: { colDef: { field: string }; data: SymbolContractCompositeModel }) => {
                            const actionField = currentColumnDefinitions[currentColumnDefinitions.length - 1].field ?? '';
                            if (event.colDef.field !== actionField) {
                                selectRow(event.data.rowIndex);
                            }
                        }}
                        onGridReady={setOnGridReady}
                        gridDataKey={props.exchangeCommoditySelection}
                        columnIdsToIgnoreUserStore={['previousSettlementPrice']}
                        testId="FuturesPricesTable"
                    />
                    <ShowMoreAccordian
                        showAccordian={symbolContractCompositeModels.length > rowsToShow}
                        showMore={showMore}
                        handleShowMoreToggle={(value) => setShowMore(value)}
                        additionalText={
                            ExchangeSettings[props.exchangeCommoditySelection.exchange as ExchangeType].hasRealtimeData ||
                            realtimeExchanges.some((x) => x === props.exchangeCommoditySelection.exchange)
                                ? translations.futures.text.realtimeFooter
                                : translations.futures.text.delayedFooter
                        }
                    />
                    <div className={styles.futures_prices_chart_container}>
                        <FuturesPricesChart
                            exchangeCommoditySelection={props.exchangeCommoditySelection}
                            symbolContracts={symbolContractSelections}
                            currency={props.currency}
                            unitOfMeasure={props.unitOfMeasure}
                            hideRollingOption={symbolModel && hideRollingContractCategories.indexOf(symbolModel.symbolCategory) >= 0}
                            testId="FuturesPricesChart"
                            track={{
                                eventCategory: 'Futures',
                                eventAction: 'FuturesPricesChart',
                            }}
                        />
                    </div>
                    <FuturesPricesComparisonChart
                        exchangeCommoditySelection={props.exchangeCommoditySelection}
                        currency={props.currency}
                        unitOfMeasure={props.unitOfMeasure}
                        testId="FuturesPricesComparisonChart"
                        track={{
                            eventCategory: 'Futures',
                            eventAction: 'FuturesPricesComparisonChart',
                        }}
                    />
                    <div className={styles.futures_prices_chart_container_small}>
                        <FuturesPricesForwardCurveChart
                            exchangeCommoditySelection={props.exchangeCommoditySelection}
                            currency={props.currency}
                            unitOfMeasure={props.unitOfMeasure}
                            testId="FuturesPricesForwardCurveChart"
                            track={{
                                eventCategory: 'Futures',
                                eventAction: 'FuturesPricesForwardCurveChart',
                            }}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default FuturesPricesTable;
